import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { usePixelTracking } from "@/hooks/usePixelTracking";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Send, Bot, User } from "lucide-react";
import { toast } from "sonner";

// Function to strip HTML tags and clean the text
const stripHtml = (html: string): string => {
  // Remove HTML tags
  let text = html.replace(/<[^>]*>/g, '');
  // Decode HTML entities
  text = text.replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  return text.trim();
};

// Function to render basic markdown (bold, italic, emojis)
const renderMarkdown = (text: string): React.ReactNode => {
  // First strip any HTML
  const cleanText = stripHtml(text);
  
  // Split by markdown patterns and render
  const parts: React.ReactNode[] = [];
  let remaining = cleanText;
  let key = 0;
  
  // Process bold **text** and italic *text*
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
  let lastIndex = 0;
  let match;
  
  while ((match = regex.exec(cleanText)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(cleanText.slice(lastIndex, match.index));
    }
    
    // Add formatted text
    if (match[2]) {
      // Bold **text**
      parts.push(<strong key={key++}>{match[2]}</strong>);
    } else if (match[3]) {
      // Italic *text*
      parts.push(<em key={key++}>{match[3]}</em>);
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < cleanText.length) {
    parts.push(cleanText.slice(lastIndex));
  }
  
  return parts.length > 0 ? parts : cleanText;
};

type FieldType = "email" | "phone" | "full_name" | "first_name" | "job_title" | "company" | "custom_text" | "custom_select";

interface FormField {
  id: string;
  field_type: FieldType;
  label: string;
  placeholder: string | null;
  is_required: boolean | null;
  options: string[] | null;
  sort_order: number;
}

interface FormData {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  logo_url: string | null;
  color_primary: string | null;
  color_secondary: string | null;
  color_accent: string | null;
  color_text_dark: string | null;
  color_text_light: string | null;
  thank_you_message: string | null;
  ai_question_count: number | null;
  ai_example_questions: string[] | null;
  ai_final_prompt: string | null;
  ai_output_word_limit: number | null;
  ga_pixel_id: string | null;
  meta_pixel_id: string | null;
  reward_type: string | null;
  reward_description: string | null;
  reward_file_url: string | null;
  vsl_youtube_url?: string | null;
}

interface ChatMessage {
  id: string;
  type: "bot" | "user";
  content: string;
  inputType?: "text" | "email" | "tel" | "textarea" | "select";
  options?: string[];
  fieldId?: string;
  isAiGenerated?: boolean;
}

interface ResponseItem {
  question: string;
  answer: string;
  isAiGenerated: boolean;
}

export default function FormPublic() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormData | null>(null);
  const [fields, setFields] = useState<FormField[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  
  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentInput, setCurrentInput] = useState("");
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [waitingForInput, setWaitingForInput] = useState(false);
  const [currentInputType, setCurrentInputType] = useState<"text" | "email" | "tel" | "textarea" | "select">("text");
  const [currentOptions, setCurrentOptions] = useState<string[]>([]);
  const [currentFieldRequired, setCurrentFieldRequired] = useState(true);
  
  // Session state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [leadEmail, setLeadEmail] = useState<string>("");
  const [allResponses, setAllResponses] = useState<ResponseItem[]>([]);
  
  // AI state
  const [isAiPhase, setIsAiPhase] = useState(false);
  const [aiQuestionIndex, setAiQuestionIndex] = useState(0);
  
  // Refs
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // Pixel Tracking
  const { trackFormStart, trackFormComplete, trackPdfDownload } = usePixelTracking({
    gaPixelId: form?.ga_pixel_id,
    metaPixelId: form?.meta_pixel_id,
  });

  // Auto scroll to bottom
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  // Focus input when waiting
  useEffect(() => {
    if (waitingForInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [waitingForInput]);

  useEffect(() => {
    if (slug) {
      fetchForm();
    }
  }, [slug]);

  const fetchForm = async (retries = 2) => {
    try {
      const { data: formData, error: formError } = await supabase
        .from("forms")
        .select("*")
        .eq("slug", slug)
        .eq("is_active", true)
        .maybeSingle();

      if (formError) {
        console.error("Error fetching form:", formError);
        if (retries > 0) {
          await new Promise(r => setTimeout(r, 1000));
          return fetchForm(retries - 1);
        }
        setNotFound(true);
        return;
      }

      if (!formData) {
        setNotFound(true);
        return;
      }

      setForm(formData);

      const { data: fieldsData, error: fieldsError } = await supabase
        .from("form_fields")
        .select("*")
        .eq("form_id", formData.id)
        .order("sort_order", { ascending: true });

      if (fieldsError) throw fieldsError;

      setFields(fieldsData || []);
      
      // Start the conversation
      setTimeout(() => startConversation(formData, fieldsData || []), 500);
    } catch (error) {
      console.error("Error fetching form:", error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  // Track form start when conversation begins
  const handleStartConversation = (formData: FormData, formFields: FormField[]) => {
    trackFormStart();
    startConversation(formData, formFields);
  };

  const addBotMessage = async (content: string, inputType?: "text" | "email" | "tel" | "textarea" | "select", options?: string[], fieldId?: string) => {
    setIsTyping(true);
    setWaitingForInput(false);
    
    // Simulate typing delay
    await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 500));
    
    const newMessage: ChatMessage = {
      id: crypto.randomUUID(),
      type: "bot",
      content,
      inputType,
      options,
      fieldId,
    };
    
    setMessages(prev => [...prev, newMessage]);
    setIsTyping(false);
    
    if (inputType) {
      setCurrentInputType(inputType);
      setCurrentOptions(options || []);
      setWaitingForInput(true);
    }
  };

  const addUserMessage = (content: string) => {
    const newMessage: ChatMessage = {
      id: crypto.randomUUID(),
      type: "user",
      content,
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const startConversation = async (formData: FormData, formFields: FormField[]) => {
    // Track form start
    trackFormStart();
    
    // Welcome message
    await addBotMessage(`Olá! 👋 Bem-vindo ao ${formData.title}.`);
    
    if (formData.description) {
      await addBotMessage(formData.description);
    }
    
    await addBotMessage("Vou te fazer algumas perguntas para criar algo especial para você. Vamos começar?");
    
    // Ask first field question
    if (formFields.length > 0) {
      const firstField = formFields[0];
      await askFieldQuestion(firstField);
    }
  };

  const getInputTypeFromField = (fieldType: FieldType): "text" | "email" | "tel" | "textarea" | "select" => {
    switch (fieldType) {
      case "email":
        return "email";
      case "phone":
        return "tel";
      case "custom_select":
        return "select";
      default:
        return "text";
    }
  };

  const askFieldQuestion = async (field: FormField) => {
    const inputType = getInputTypeFromField(field.field_type);
    setCurrentFieldRequired(field.is_required ?? true);
    await addBotMessage(
      field.label + (field.is_required ? "" : " (opcional)"),
      inputType,
      field.options || undefined,
      field.id
    );
  };

  const handleSendMessage = async () => {
    if (!currentInput.trim() || !waitingForInput) return;

    const answer = currentInput.trim();
    addUserMessage(answer);
    setCurrentInput("");
    setWaitingForInput(false);

    if (!isAiPhase) {
      // Static fields phase
      await handleStaticFieldAnswer(answer);
    } else {
      // AI questions phase
      await handleAiAnswer(answer);
    }
  };

  const handleSkipQuestion = async () => {
    if (!waitingForInput || currentFieldRequired) return;
    
    addUserMessage("(pulado)");
    setCurrentInput("");
    setWaitingForInput(false);
    
    // Move to next field without storing response
    const nextIndex = currentFieldIndex + 1;
    
    if (nextIndex < fields.length) {
      setCurrentFieldIndex(nextIndex);
      await askFieldQuestion(fields[nextIndex]);
    } else {
      // All static fields done, create session and start AI phase
      await createSessionAndStartAi(allResponses);
    }
  };

  const handleStaticFieldAnswer = async (answer: string) => {
    const currentField = fields[currentFieldIndex];
    
    // Store response
    const newResponse: ResponseItem = {
      question: currentField.label,
      answer,
      isAiGenerated: false,
    };
    
    const updatedResponses = [...allResponses, newResponse];
    setAllResponses(updatedResponses);
    
    // Check if it's email field
    if (currentField.field_type === "email") {
      setLeadEmail(answer);
    }
    
    const nextIndex = currentFieldIndex + 1;
    
    if (nextIndex < fields.length) {
      // Ask next field question
      setCurrentFieldIndex(nextIndex);
      await askFieldQuestion(fields[nextIndex]);
    } else {
      // All static fields done, create session and start AI phase
      await createSessionAndStartAi(updatedResponses);
    }
  };

  const createSessionAndStartAi = async (responses: ResponseItem[]) => {
    if (!form) return;

    try {
      // Create session using secure RPC function
      const email = leadEmail || "unknown@lead.com";
      const { data: newSessionId, error: sessionError } = await supabase
        .rpc('create_form_session', {
          p_form_id: form.id,
          p_lead_email: email,
        });

      if (sessionError) throw sessionError;
      if (!newSessionId) throw new Error("Failed to create session");

      setSessionId(newSessionId);

      // Save initial responses
      const dbResponses = responses.map((r, index) => ({
        session_id: newSessionId,
        question: r.question.substring(0, 500),
        answer: r.answer.substring(0, 10000),
        question_order: index + 1,
        is_ai_generated: false,
      }));

      const { error: responsesError } = await supabase
        .from("form_responses")
        .insert(dbResponses);

      if (responsesError) throw responsesError;

      // Check if form has AI questions
      // For file rewards, use reward_description as the context for questions
      const hasAiQuestions = form.ai_question_count && form.ai_question_count > 0;
      const hasAiContext = form.reward_type === "file" 
        ? !!form.reward_description 
        : !!form.ai_final_prompt;

      if (hasAiQuestions && hasAiContext) {
        setIsAiPhase(true);
        await addBotMessage("Ótimo! Agora vou te fazer algumas perguntas mais específicas para personalizar melhor seu conteúdo...");
        await generateNextAiQuestion(responses, 0, newSessionId);
      } else {
        // No AI questions, complete
        await completeSession(newSessionId, responses);
      }
    } catch (error) {
      console.error("Error creating session:", error);
      toast.error("Erro ao salvar respostas");
    }
  };

  const generateNextAiQuestion = async (
    responses: ResponseItem[],
    questionIndex: number,
    currentSessionId: string
  ) => {
    if (!form) return;

    const remainingQuestions = (form.ai_question_count || 5) - questionIndex;
    
    if (remainingQuestions <= 0) {
      await completeSession(currentSessionId, responses);
      return;
    }

    setIsTyping(true);

    try {
      // For file rewards, use reward_description as the prompt context
      const promptContext = form.reward_type === "file" 
        ? form.reward_description 
        : form.ai_final_prompt;

      const { data, error } = await supabase.functions.invoke("generate-question", {
        body: {
          formConfig: {
            title: form.title,
            description: form.description,
            aiExampleQuestions: form.ai_example_questions,
            aiFinalPrompt: promptContext,
          },
          previousResponses: responses.map((r) => ({
            question: r.question,
            answer: r.answer,
          })),
          remainingQuestions,
          leadEmail,
          formId: form.id,
          sessionId: currentSessionId,
        },
      });

      setIsTyping(false);

      if (error) {
        console.error("Error generating question:", error);
        
        // Check if limit was reached
        if (error.message?.includes("403") || error.message?.includes("limitReached")) {
          toast.error("O formulário atingiu o limite. Finalizando...");
          await completeSession(currentSessionId, responses);
          return;
        }
        
        toast.error("Erro ao gerar pergunta. Tentando novamente...");
        setTimeout(() => generateNextAiQuestion(responses, questionIndex, currentSessionId), 2000);
        return;
      }

      if (data?.question) {
        const inputType = data.inputType === "select" ? "select" : data.inputType === "textarea" ? "textarea" : "text";
        await addBotMessage(data.question, inputType, data.options);
        setAiQuestionIndex(questionIndex);
      } else {
        await completeSession(currentSessionId, responses);
      }
    } catch (error) {
      console.error("Error calling generate-question:", error);
      setIsTyping(false);
      toast.error("Erro ao gerar pergunta");
    }
  };

  const handleAiAnswer = async (answer: string) => {
    if (!sessionId || !form) return;

    try {
      // Get the last bot message as the question
      const lastBotMessage = [...messages].reverse().find(m => m.type === "bot");
      const question = lastBotMessage?.content || "Pergunta";

      // Save AI response to database
      const questionOrder = fields.length + aiQuestionIndex + 1;
      
      const { error } = await supabase.from("form_responses").insert({
        session_id: sessionId,
        question,
        answer,
        question_order: questionOrder,
        is_ai_generated: true,
      });

      if (error) throw error;

      // Update responses array
      const newResponse: ResponseItem = {
        question,
        answer,
        isAiGenerated: true,
      };

      const updatedResponses = [...allResponses, newResponse];
      setAllResponses(updatedResponses);

      // Update session current_question_index using secure RPC
      await supabase.rpc('update_form_session', {
        p_session_id: sessionId,
        p_current_question_index: aiQuestionIndex + 1
      });

      // Generate next question or finish
      await generateNextAiQuestion(updatedResponses, aiQuestionIndex + 1, sessionId);
    } catch (error) {
      console.error("Error saving AI answer:", error);
      toast.error("Erro ao salvar resposta");
    }
  };

  const completeSession = async (currentSessionId: string, responses: ResponseItem[]) => {
    if (!form) return;

    // Track form completion
    trackFormComplete(leadEmail);

    await addBotMessage("Perfeito! Estou preparando seu conteúdo personalizado... 🎁");

    let pdfHtml: string | null = null;
    let pdfUrl: string | null = null;
    let rewardFileUrl: string | null = null;

    // Check if this is a file reward type
    const isFileReward = form.reward_type === "file" && form.reward_file_url;

    if (isFileReward) {
      // For file rewards, just use the uploaded file URL
      rewardFileUrl = form.reward_file_url;
    } else {
      // For AI rewards, generate PDF
      try {
        const { data, error } = await supabase.functions.invoke("generate-pdf", {
          body: {
            formConfig: {
              title: form.title,
              description: form.description,
              aiFinalPrompt: form.ai_final_prompt,
              logoUrl: form.logo_url,
              colorPrimary: form.color_primary || "#6366f1",
              colorSecondary: form.color_secondary || "#f1f5f9",
              colorAccent: form.color_accent || "#10b981",
            },
            responses: responses.map((r) => ({
              question: r.question,
              answer: r.answer,
            })),
            leadEmail,
            sessionId: currentSessionId,
            outputWordLimit: form.ai_output_word_limit || 2000,
          },
        });

        if (error) {
          console.error("Error generating PDF:", error);
          if (error.message?.includes("429") || error.message?.includes("Rate")) {
            toast.error("Limite de requisições excedido. Aguarde um momento...");
            setTimeout(() => completeSession(currentSessionId, responses), 3000);
            return;
          }
          // Continue without PDF - user can still see thank you page
        } else {
          pdfHtml = data?.fullHtml || null;
          pdfUrl = data?.pdfUrl || null;
        }
      } catch (error) {
        console.error("Error calling generate-pdf:", error);
        // Continue without PDF
      }
    }

    try {
      // Update session to completed using secure RPC
      const { data: updateResult, error: updateError } = await supabase.rpc('update_form_session', {
        p_session_id: currentSessionId,
        p_status: 'completed' as const
      });
      
      if (updateError) {
        console.error("Error updating session to completed:", updateError);
      } else {
        console.log("Session marked as completed:", currentSessionId, updateResult);
      }
    } catch (error) {
      console.error("Error updating session:", error);
    }

    // Navigate to result page with data
    navigate(`/f/${slug}/resultado`, {
      state: {
        form,
        pdfHtml,
        pdfUrl,
        rewardFileUrl,
        responses,
        sessionId: currentSessionId,
        leadEmail,
      },
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
        <div className="w-full max-w-lg space-y-4">
          <Skeleton className="h-12 w-12 rounded-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-muted flex items-center justify-center">
            <Bot className="h-8 w-8 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-display font-bold">Formulário não encontrado</h1>
          <p className="text-muted-foreground">
            O formulário que você está procurando não existe ou foi desativado.
          </p>
        </div>
      </div>
    );
  }

  const primaryColor = form?.color_primary || "#6366f1";

  return (
    <div 
      className="h-screen flex flex-col overflow-hidden"
      style={{
        background: `linear-gradient(180deg, hsl(240 20% 98%) 0%, ${primaryColor}10 100%)`,
      }}
    >
      {/* Header */}
      <header className="sticky top-0 z-10 glass border-b px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          {form?.logo_url ? (
            <img
              src={form.logo_url}
              alt="Logo"
              className="h-10 w-10 rounded-full object-cover"
            />
          ) : (
            <div 
              className="h-10 w-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: primaryColor }}
            >
              <Bot className="h-5 w-5 text-white" />
            </div>
          )}
          <div>
            <h1 className="font-display font-semibold text-sm">{form?.title}</h1>
            <p className="text-xs text-muted-foreground">Online agora</p>
          </div>
        </div>
      </header>

      {/* Chat Messages */}
      <div 
        ref={chatContainerRef}
        className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4"
      >
        <div className="max-w-lg mx-auto space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex gap-2 animate-slide-up ${
                message.type === "user" ? "justify-end" : "justify-start"
              }`}
            >
              {message.type === "bot" && (
                <div 
                  className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Bot className="h-4 w-4 text-white" />
                </div>
              )}
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                  message.type === "user"
                    ? "bg-primary text-primary-foreground rounded-br-md"
                    : "bg-card border border-border rounded-bl-md"
                }`}
                style={message.type === "user" ? { backgroundColor: primaryColor } : undefined}
              >
                <p className="text-sm whitespace-pre-wrap">{renderMarkdown(message.content)}</p>
              </div>
              {message.type === "user" && (
                <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center bg-muted">
                  <User className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
            </div>
          ))}
          
          {/* Typing indicator */}
          {isTyping && (
            <div className="flex gap-2 justify-start animate-fade-in">
              <div 
                className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center"
                style={{ backgroundColor: primaryColor }}
              >
                <Bot className="h-4 w-4 text-white" />
              </div>
              <div className="bg-card border border-border rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Input Area */}
      <div className="shrink-0 glass border-t p-4">
        <div className="max-w-lg mx-auto">
          {waitingForInput && currentInputType === "select" && currentOptions.length > 0 ? (
            <div className="space-y-2">
              <div className="flex flex-col gap-2">
                {currentOptions.map((option) => (
                  <Button
                    key={option}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCurrentInput(option);
                      setTimeout(() => {
                        addUserMessage(option);
                        setCurrentInput("");
                        setWaitingForInput(false);
                        if (!isAiPhase) {
                          handleStaticFieldAnswer(option);
                        } else {
                          handleAiAnswer(option);
                        }
                      }, 100);
                    }}
                    className="rounded-xl h-auto py-2.5 px-4 text-left whitespace-normal text-wrap"
                  >
                    {option}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              {currentInputType === "textarea" ? (
                <Textarea
                  ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                  value={currentInput}
                  onChange={(e) => setCurrentInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Digite sua mensagem..."
                  disabled={!waitingForInput}
                  rows={2}
                  className="flex-1 resize-none rounded-2xl"
                />
              ) : (
                <Input
                  ref={inputRef as React.RefObject<HTMLInputElement>}
                  type={currentInputType}
                  value={currentInput}
                  onChange={(e) => setCurrentInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={waitingForInput ? "Digite sua resposta..." : "Aguarde..."}
                  disabled={!waitingForInput}
                  className="flex-1 rounded-full"
                />
              )}
              {!isAiPhase && !currentFieldRequired && waitingForInput && (
                <Button
                  onClick={handleSkipQuestion}
                  variant="ghost"
                  size="sm"
                  className="rounded-full shrink-0 text-muted-foreground"
                >
                  Pular
                </Button>
              )}
              <Button
                onClick={handleSendMessage}
                disabled={!waitingForInput || !currentInput.trim()}
                size="icon"
                className="rounded-full shrink-0"
                style={{ backgroundColor: waitingForInput && currentInput.trim() ? primaryColor : undefined }}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
